const PEAK_WINDOW_MS = 2000;
const OPEN_TAU_MS = 50;
const CLOSE_TAU_MS = 40;
const FFT_SIZE = 1024;

const HAPPY = 1;
const SAD = 3;

export function computeRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let total = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const value = samples[index] ?? 0;
    total += value * value;
  }
  return Math.sqrt(total / samples.length);
}

export function computeZeroCrossingRate(samples: Float32Array): number {
  if (samples.length < 2) return 0;
  let crossings = 0;
  for (let index = 1; index < samples.length; index += 1) {
    if (((samples[index] ?? 0) < 0) !== ((samples[index - 1] ?? 0) < 0)) crossings += 1;
  }
  return crossings / (samples.length - 1);
}

/** Normalised to [0, 1] over the 400Hz–3kHz band the drive protocol cares about. */
export function computeSpectralCentroid(magnitudes: Float32Array, sampleRate: number): number {
  if (magnitudes.length === 0) return 0.5;
  const binWidth = sampleRate / 2 / magnitudes.length;
  let weighted = 0;
  let total = 0;
  for (let index = 0; index < magnitudes.length; index += 1) {
    const magnitude = magnitudes[index] ?? 0;
    weighted += index * binWidth * magnitude;
    total += magnitude;
  }
  if (total <= 1e-9) return 0.5;
  return clamp((weighted / total - 400) / 2600);
}

/**
 * Mirrors AudioFeatureExtractor in src/avatar_live/agent/drive/audio_feature.py so the
 * locally analysed mouth matches what the server would have produced for the same audio.
 */
export class MouthEnvelope {
  private smoothedOpen = 0;
  private noiseFloor = 0.008;
  private readonly peaks: { atMs: number; value: number }[] = [];
  private elapsedMs = 0;
  private readonly mouth = new Float32Array(8);

  reset(): void {
    this.smoothedOpen = 0;
    this.peaks.length = 0;
    this.elapsedMs = 0;
  }

  update(
    { rms, centroid, zeroCrossingRate, emotion }: {
      rms: number;
      centroid: number;
      zeroCrossingRate: number;
      emotion: number;
    },
    dtMs: number,
  ): Float32Array {
    const step = Math.min(200, Math.max(1, dtMs));
    this.elapsedMs += step;

    this.peaks.push({ atMs: this.elapsedMs, value: rms });
    while (this.peaks.length > 0 && this.elapsedMs - (this.peaks[0]?.atMs ?? 0) > PEAK_WINDOW_MS) {
      this.peaks.shift();
    }
    let peak = 0.1;
    for (const entry of this.peaks) peak = Math.max(peak, entry.value);
    peak = Math.max(peak, this.noiseFloor + 0.03);

    if (rms < this.noiseFloor * 1.5) this.noiseFloor = 0.995 * this.noiseFloor + 0.005 * rms;

    let normalized = clamp((rms - this.noiseFloor) / (peak - this.noiseFloor));
    if (rms <= this.noiseFloor * 1.2) normalized = 0;

    const tau = normalized < this.smoothedOpen ? CLOSE_TAU_MS : OPEN_TAU_MS;
    this.smoothedOpen += (1 - Math.exp(-step / tau)) * (normalized - this.smoothedOpen);

    const consonantClose = clamp((zeroCrossingRate - 0.16) * 3.5) * this.smoothedOpen;
    const open = clamp(this.smoothedOpen * (1 - 0.35 * consonantClose));
    const happy = clamp(1 - Math.abs(emotion - HAPPY));
    const sad = clamp(1 - Math.abs(emotion - SAD));

    this.mouth[0] = open;
    this.mouth[1] = clamp(centroid * open);
    this.mouth[2] = clamp((1 - centroid) * open);
    this.mouth[3] = open * (0.08 + 0.47 * happy);
    this.mouth[4] = open * (0.55 * sad);
    this.mouth[5] = open * 0.8;
    this.mouth[6] = consonantClose;
    this.mouth[7] = open * 0.9;
    return this.mouth;
  }
}

/**
 * Drives the mouth from the audio that is actually reaching the speakers, which keeps the
 * visemes aligned with playback regardless of jitter-buffer depth or data-channel skew.
 */
export class AudioLipSync {
  private readonly context: AudioContext;
  private readonly ownsContext: boolean;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly analyser: AnalyserNode;
  private readonly silence: GainNode;
  private readonly timeDomain: Float32Array<ArrayBuffer>;
  private readonly decibels: Float32Array<ArrayBuffer>;
  private readonly magnitudes: Float32Array<ArrayBuffer>;
  private readonly envelope = new MouthEnvelope();
  private lastSampledAtMs: number | undefined;
  private closed = false;

  constructor(stream: MediaStream, context?: AudioContext) {
    this.ownsContext = context === undefined;
    this.context = context ?? new AudioContext({ latencyHint: 'interactive' });
    this.source = this.context.createMediaStreamSource(stream);
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = FFT_SIZE;
    this.analyser.smoothingTimeConstant = 0.4;
    // A muted sink keeps the graph pulled without emitting a second copy of the audio.
    this.silence = this.context.createGain();
    this.silence.gain.value = 0;
    this.source.connect(this.analyser);
    this.analyser.connect(this.silence);
    this.silence.connect(this.context.destination);
    this.timeDomain = new Float32Array(this.analyser.fftSize);
    this.decibels = new Float32Array(this.analyser.frequencyBinCount);
    this.magnitudes = new Float32Array(this.analyser.frequencyBinCount);
  }

  async resume(): Promise<void> {
    if (!this.closed && this.context.state === 'suspended') await this.context.resume();
  }

  sample(emotion: number, nowMs = performance.now()): Float32Array | undefined {
    if (this.closed) return undefined;
    const dtMs = this.lastSampledAtMs === undefined ? 16 : nowMs - this.lastSampledAtMs;
    this.lastSampledAtMs = nowMs;

    this.analyser.getFloatTimeDomainData(this.timeDomain);
    this.analyser.getFloatFrequencyData(this.decibels);
    for (let index = 0; index < this.magnitudes.length; index += 1) {
      this.magnitudes[index] = 10 ** ((this.decibels[index] ?? -Infinity) / 20);
    }

    return this.envelope.update(
      {
        rms: computeRms(this.timeDomain),
        centroid: computeSpectralCentroid(this.magnitudes, this.context.sampleRate),
        zeroCrossingRate: computeZeroCrossingRate(this.timeDomain),
        emotion,
      },
      dtMs,
    );
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.source.disconnect();
    this.analyser.disconnect();
    this.silence.disconnect();
    if (this.ownsContext) await this.context.close();
  }
}

function clamp(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}
